import React, { useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { getCompanies, getClientsByAgencyId } from '../../features/companies/companiesSlice';
import { useTranslation } from 'react-i18next';
import companiesService from '../../features/companies/companiesService';

interface CompanyFilterProps {
  selectedAgency: number | undefined;
  selectedClient: number | undefined;
  onAgencyChange: (agencyId: number | undefined) => void;
  onClientChange: (clientId: number | undefined) => void;
}

const CompanyFilter: React.FC<CompanyFilterProps> = ({
  selectedAgency,
  selectedClient,
  onAgencyChange,
  onClientChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const { companies, clients, isLoading } = useSelector((state: RootState) => state.companies);
  
  // Kullanıcı rollerini kontrol et
  const userRoles = user?.roles || [];
  const isMarketingSpecialist = userRoles.includes('marketing_specialist');
  const isAgencyOwner = userRoles.includes('agency_owner');
  
  // Tüm şirketleri yükle
  useEffect(() => {
    if (!companies.length) {
      if (isMarketingSpecialist) {
        // Marketing specialist'lar için sadece bağlı oldukları şirketleri yükle
        loadUserCompanies();
      } else {
        // Agency owner'lar için tüm şirketleri yükle
        dispatch(getCompanies({}));
      }
    }
  }, [dispatch, companies.length, isMarketingSpecialist]);
  
  // Marketing specialist'lar için şirketleri yükle
  const loadUserCompanies = async () => {
    try {
      const userCompanies = await companiesService.getMyAgencyCompanies();
      // Redux store'a yükle
      dispatch({ type: 'companies/setCompanies', payload: userCompanies });
    } catch (error) {
      console.error('Error loading user companies:', error);
    }
  };
  
  // Agency seçildiğinde client'ları getir
  useEffect(() => {
    if (selectedAgency) {
      dispatch(getClientsByAgencyId(selectedAgency))
        .unwrap()
        .catch(error => {
          console.error("Error fetching clients:", error);
        });
    }
  }, [dispatch, selectedAgency]);
  
  // Filtreleme - Marketing specialist'lar için sadece bağlı oldukları şirketler
  const agencies = companies.filter(company => {
    if (isMarketingSpecialist) {
      // Marketing specialist'lar sadece bağlı oldukları agency'leri görebilir
      return company.company_type === 'agency';
    } else {
      // Agency owner'lar tüm agency'leri görebilir
      return company.company_type === 'agency';
    }
  });
  
  // Agency değiştiğinde Client değerinin sıfırlanması
  const handleAgencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const agencyId = e.target.value ? Number(e.target.value) : undefined;
    onAgencyChange(agencyId);
    
    // Agency değiştiğinde Client'ı sıfırla
    onClientChange(undefined);
  };
  
  return (
    <Row>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>{t('Agency')}</Form.Label>
          <Form.Select
            value={selectedAgency || ''}
            onChange={handleAgencyChange}
            disabled={isLoading}
          >
            <option value="">{t('All Agencies')}</option>
            {agencies.map(agency => (
              <option key={agency.id} value={agency.id}>
                {agency.company_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
      
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>{t('Client Company')} *</Form.Label>
          <Form.Select
            value={selectedClient || ''}
            onChange={(e) => onClientChange(e.target.value ? Number(e.target.value) : undefined)}
            disabled={isLoading || !selectedAgency}
            required
          >
            <option value="">{t('Select Client Company')}</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.company_name}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            {t('Please select a client company to continue')}
          </Form.Text>
        </Form.Group>
      </Col>
    </Row>
  );
};

export default CompanyFilter; 